import React, { useState, useCallback, useEffect } from 'react';
import ChevronUpIcon from 'mdi-react/ChevronUpIcon';
import { MultiValue } from 'react-select';
import { useDispatch, useSelector } from 'react-redux';
import MagnifyIcon from 'mdi-react/MagnifyIcon';

import Button from '@xcritical/button';
import Select from '@xcritical/select';
import { Popover } from '@xcritical/popover';

import { ActionsButtons } from '@crm-framework/actions-buttons';
import { IconButton } from '@crm-framework/icon-button';
import { LSHelper } from '@crm-framework/utils';

import { getIsEditModeOn, gridLayoutActions } from '../store';
import { IStateWithGridLayout } from '../models';

import {
  ACTIONS_BUTTONS_WRAPPER,
  IconStyled,
  IGridPanelsProps,
  IPanelOption,
} from './modelsAndConsts';

const DropdownIndicator: React.FC = () => (
  <IconButton appearance="default" icon={MagnifyIcon} />
);

export const RGLMenuButton: React.FC<IGridPanelsProps> = ({
  panelsList,
  isDisabled,
  storageName,
}) => {
  const dispatch = useDispatch();
  const isShownButton = useSelector((state: IStateWithGridLayout) =>
    getIsEditModeOn(state, storageName)
  );
  const [inputValue, setInputValue] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const visiblePanelStorageName = `${storageName}-visible-panels`;
  const [selectedOptions, setSelectedOptions] =
    useState<MultiValue<IPanelOption>>(panelsList);
  const isAllOptionsSelected = panelsList.length === selectedOptions.length;

  useEffect(() => {
    const visiblePanels = LSHelper.getObject(visiblePanelStorageName);
    let newSelectedOptions = panelsList;
    let panelsStoreList = panelsList.map((panel) => panel.value);

    if (visiblePanels) {
      panelsStoreList = visiblePanels as string[];
      newSelectedOptions = panelsList.reduce(
        (list, item) =>
          panelsStoreList.includes(item.value) ? [...list, item] : list,
        []
      );
    }

    setSelectedOptions(newSelectedOptions);
    dispatch(
      gridLayoutActions.setVisiblePanelsList({
        name: storageName,
        value: panelsStoreList,
      })
    );
  }, []);

  const onDone = useCallback(() => {
    const visiblePanelsList = selectedOptions.reduce(
      (list, item) => [...list, item.value],
      []
    );
    dispatch(
      gridLayoutActions.setVisiblePanelsList({
        name: storageName,
        value: visiblePanelsList,
      })
    );
    dispatch(gridLayoutActions.setIsLoading(true));
    setIsVisible(false);
    LSHelper.setItem(visiblePanelStorageName, visiblePanelsList);
    setInputValue('');
  }, [selectedOptions, storageName]);

  const onCancelClick = useCallback(() => {
    setIsVisible(false);
    setInputValue('');
  }, []);

  const onInputChange = useCallback((newInputValue, { action }) => {
    if (action === 'input-change') {
      setInputValue(newInputValue);
    }
  }, []);

  const onVisibleChange = useCallback(
    (visible: boolean) => {
      if (!isDisabled) {
        setIsVisible(visible);
      }
    },
    [isDisabled]
  );

  const onToggleAll = useCallback(() => {
    if (isAllOptionsSelected) {
      setSelectedOptions([]);
    } else {
      setSelectedOptions(panelsList);
    }
  }, [isAllOptionsSelected, panelsList]);

  if (!isShownButton) return <></>;

  return (
    <Popover
      trigger="click"
      position="bottom right"
      appearance="itemList"
      withArrow={false}
      visible={isVisible}
      onVisibleChange={onVisibleChange}
      content={
        <>
          <Select
            className="react-grid-layout-dropdown"
            autoFocus
            isSearchable
            menuIsOpen
            isMulti
            placeholder="Search..."
            appearance="dropdown"
            backspaceRemovesValue={false}
            controlShouldRenderValue={false}
            hideSelectedOptions={false}
            isClearable={false}
            options={panelsList}
            value={selectedOptions}
            onChange={setSelectedOptions}
            components={{ DropdownIndicator, IndicatorSeparator: null }}
            inputValue={inputValue}
            onInputChange={onInputChange}
          />

          <ActionsButtons
            wrapperStyle={ACTIONS_BUTTONS_WRAPPER}
            buttons={[
              {
                title: isAllOptionsSelected ? 'Unselect all' : 'Select all',
                type: 'button',
                appearance: 'link',
                onClick: onToggleAll,
              },
              {
                title: 'Cancel',
                type: 'button',
                appearance: 'link',
                onClick: onCancelClick,
              },
              {
                title: 'Done',
                type: 'button',
                appearance: 'primary',
                onClick: onDone,
              },
            ]}
          />
        </>
      }>
      <Button
        disabled={isDisabled}
        selected={isVisible}
        baseAppearance="compact"
        shouldFitContent
        postfix={
          <IconStyled $isOpen={isVisible}>
            <ChevronUpIcon size={18} />
          </IconStyled>
        }>
        Panels
      </Button>
    </Popover>
  );
};
